'use client';

/**
 * Widget "Save to…" dialog — the HOST-side destination picker behind
 * `host.documents.saveDialog` (Save a copy). Portal chrome rendered OVER the
 * canvas, never inside the widget iframe: the widget only learns the
 * { parentId, name } the user confirmed.
 *
 * Opened via a window CustomEvent('widget-save-dialog') whose detail carries
 * { defaultName?, defaultFolderId?, title?, resolve } — the bridge awaits
 * `resolve({ parentId, name })`. Cancel / Esc resolves null.
 *
 * The dialog itself is the shared {@link PortalFileDialog} in `save` mode —
 * the SAME browser (breadcrumbs from "My files", folders + files, search)
 * that `host.content.pick` shows in `open` mode. It starts at the current
 * workspace folder with the root one click up.
 */

import { useEffect, useState } from 'react';
import { PortalFileDialog } from '@/components/workspace/portal-file-dialog';
import { useAppFrameFocusReturn } from '@/components/workspace/app-frame-focus-return';

interface SaveRequest {
  defaultName?: string;
  defaultFolderId?: string | null;
  title?: string;
  resolve: (result: { parentId: string | null; name: string } | null) => void;
}

export function WidgetSaveDialog() {
  const [req, setReq] = useState<SaveRequest | null>(null);
  // Close hands focus back to the app that asked (app-frame-focus-return.ts).
  const captureAppFocus = useAppFrameFocusReturn(!!req);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<SaveRequest>).detail;
      if (!detail || typeof detail.resolve !== 'function') return;
      captureAppFocus();
      setReq(detail);
    };
    window.addEventListener('widget-save-dialog', onOpen);
    return () => window.removeEventListener('widget-save-dialog', onOpen);
  }, [captureAppFocus]);

  if (!req) return null;

  return (
    <PortalFileDialog
      mode="save"
      title={req.title || 'Save a copy'}
      defaultName={req.defaultName || 'Untitled'}
      initialFolderId={req.defaultFolderId ?? null}
      onResolve={(result) => {
        req.resolve(
          result
            ? { parentId: result.parentId, name: result.name.trim() || (req.defaultName || 'Untitled') }
            : null,
        );
        setReq(null);
      }}
    />
  );
}
